// src/belediye/components/mesajlasma/AciliyetSureAdimi.js
// FAZ-83/S1 — ACİLİYET ve SÜRE TEK ADIMDA (iki çip satırı).
//
// Kullanıcı (2026-08-01): "süre ve aciliyet kısımlarını da aynı anda
// yapabiliriz" → "İki çip satırı, tek panel… ama burada SÜREYİ ALTA AL,
// ACİLİYET YUKARDA OLSUN."
//
// ACİLİYET NEDEN ÜSTTE — sırf düzen değil, işe yarıyor: "Kendin belirle"
// takvimi panelin EN ALTINDAN açılıyor. Aciliyet üstteyken açılış onu
// yerinden oynatmaz; ters sırada takvim açılınca aciliyet ekrandan çıkıyordu.
//
// FAZ-83/2 (aynı gün, ikinci tur): "bunlar çok iç içe olmuş. Aciliyet kısmını
// BİR SATIRMIŞ GİBİ ayarlayalım ve içinde 3 farklı çip olsun. Altındaki süre
// kısmında SÜRE YOK'u kaldıralım — atla diyince süre olmuyor zaten. Geri kalan
// seçenekleri de TEK SATIRA sığdırmaya çalışalım; olmazsa aciliyetle süreyi
// birbirinden AYIRACAK şekilde yap." → İki grup da kendi satırında, aralarında
// ayırıcı çizgi; çipler tek satırda kalacak kadar kompakt.
//
// DAVRANIŞ DEĞİŞİKLİĞİ: sihirbazın eski seçenekli adımlarında bir seçeneğe
// basmak adımı HEMEN ilerletiyordu. Burada basmak yalnız SEÇER; ilerleten
// Gönder'dir. İki soru aynı ekranda olunca başka türlü mümkün değil.
//
// Sözleşme: onDone({ priority, due_date?, due_time? }) — süre verilmezse
// due_* hiç gitmez; priority her zaman gider (varsayılan "normal").
import React, { useState } from "react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";

import { isoLocalInDays } from "../../utils/dateLocal";
import DuePicker from "./DuePicker";
import SihirbazAltSatir from "./SihirbazAltSatir";

// FAZ-55/6: "Düşük" YOK — görev verilmişse en az normaldir.
const ACILIYETLER = [
  { k: "normal", ad: "Normal" },
  { k: "yuksek", ad: "Yüksek" },
  { k: "kritik", ad: "Kritik" },
];

// FAZ-83/2: "Süre yok" KALDIRILDI — Atla zaten tam olarak onu yapıyordu ve
// iki ayrı yoldan aynı sonuca varmak satırı gereksiz uzatıyordu.
const SURELER = () => [
  { k: "bugun", ad: "Bugün", tarih: isoLocalInDays(0) },
  { k: "yarin", ad: "Yarın", tarih: isoLocalInDays(1) },
  { k: "3gun", ad: "3 gün", tarih: isoLocalInDays(3) },
  { k: "1hafta", ad: "1 hafta", tarih: isoLocalInDays(7) },
];

const uzunTarih = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "short" });
};

export default function AciliyetSureAdimi({ acc = {}, onDone, onSkip, pasif = false }) {
  // Aciliyet AÇILIŞTA "normal" seçili — zaten sistemin varsayılanı bu.
  // Seçili göstermek bir yalan değil, olanı yazmak.
  const [priority, setPriority] = useState(acc.priority || "normal");
  const [sureKey, setSureKey] = useState(acc.due_date ? "ozel" : null);
  const [tarih, setTarih] = useState(acc.due_date || null);
  const [saat, setSaat] = useState(acc.due_time || null);
  const [ozelAcik, setOzelAcik] = useState(false);

  const sureler = SURELER();

  const hazirSec = (s) => {
    setSureKey(s.k);
    setTarih(s.tarih);
    setSaat(null);
    setOzelAcik(false);
  };

  const ozelDegisti = ({ due_date, due_time }) => {
    setSureKey(due_date ? "ozel" : null);
    setTarih(due_date);
    setSaat(due_time);
  };

  const gonder = () => {
    if (pasif) return;
    const p = { priority };
    if (tarih) {
      p.due_date = tarih;
      if (saat) p.due_time = saat;
    }
    onDone(p);
  };

  const aciliyetAdi = ACILIYETLER.find((a) => a.k === priority)?.ad || "Normal";
  const sureOzeti = tarih
    ? `${uzunTarih(tarih)}${saat ? ` · ${saat}` : " · gün sonu"}`
    : "süre yok";

  return (
    <div className="msj-acilsure">
      {/* ── ACİLİYET — kendi satırı, içinde üç çip ─────────────────────── */}
      <div className="msj-alan-satir">
        <span className="msj-due-etiket">Aciliyet</span>
        <div className="msj-due-cipler msj-tek-satir">
          {ACILIYETLER.map((a) => (
            <button
              key={a.k}
              type="button"
              className={`msj-due-cip ${priority === a.k ? "is-secili" : ""}`}
              disabled={pasif}
              aria-pressed={priority === a.k}
              onClick={() => setPriority(a.k)}
            >
              {a.ad}
            </button>
          ))}
        </div>
      </div>

      {/* ── SÜRE — ayırıcının altında, takvim buradan aşağı açılır ─────── */}
      <div className="msj-alan-satir">
        <span className="msj-due-etiket">Süre</span>
        <div className="msj-due-cipler msj-tek-satir">
          {sureler.map((s) => (
            <button
              key={s.k}
              type="button"
              className={`msj-due-cip ${sureKey === s.k ? "is-secili" : ""}`}
              disabled={pasif}
              aria-pressed={sureKey === s.k}
              onClick={() => hazirSec(s)}
            >
              {s.ad}
            </button>
          ))}
          <button
            type="button"
            className={`msj-due-cip ${ozelAcik || sureKey === "ozel" ? "is-secili" : ""}`}
            disabled={pasif}
            aria-expanded={ozelAcik}
            onClick={() => setOzelAcik((v) => !v)}
          >
            <PencilSquareIcon className="msj-serit-ic" /> Kendin belirle
          </button>
        </div>
      </div>

      {ozelAcik && (
        <DuePicker
          gomulu
          initialDate={tarih || ""}
          initialTime={saat || ""}
          onDegis={ozelDegisti}
        />
      )}

      {/* Seçim ana mesaj kutusunun ORTASINDA yazılır (kullanıcı: "kendi süremi
          belirle kısmında eskisi gibi seçilen süre ana mesaj kısmının
          ortasında yazılsın"). */}
      <SihirbazAltSatir
        onSkip={onSkip}
        skipTitle="Aciliyet ve süre vermeden devam et"
        ozet={
          <>
            <b>{aciliyetAdi}</b>
            {` · ${sureOzeti}`}
          </>
        }
        onGonder={gonder}
        gonderTitle="Devam et"
        pasif={pasif}
      />
    </div>
  );
}
